import React, { useState } from 'react';
import { useJobs } from '../../hooks/useJobs';
import './JobsTable.css';

interface JobsTableProps {
  jobs: any[];
  onEdit: (job: any) => void;
  onRefresh?: () => void;
}

const JobsTable: React.FC<JobsTableProps> = ({ jobs, onEdit, onRefresh }) => {
  const { deleteJob, refetch } = useJobs();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) {
      return;
    }

    setDeletingId(id);
    const { error } = await deleteJob(id);
    if (error) {
      alert(`Error deleting job: ${error}`);
      setDeletingId(null);
    } else {
      // Explicitly refetch to ensure the list is updated
      await refetch();
      // Also call parent's refresh callback if provided
      if (onRefresh) {
        onRefresh();
      }
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getUploaderName = (email: string) => {
    const name = email.split('@')[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  if (jobs.length === 0) {
    return (
      <div className="jobs-table-empty">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
        <h3>No jobs posted yet</h3>
        <p>Create your first job posting to get started</p>
      </div>
    );
  }

  return (
    <div className="jobs-table-container">
      <div className="jobs-table-header">
        <h2>Jobs ({jobs.length})</h2>
      </div>
      <div className="jobs-table-wrapper">
        <table className="jobs-table">
          <thead>
            <tr>
              <th>Title (NL)</th>
              <th>Discipline</th>
              <th>Location</th>
              <th>Salary</th>
              <th>Posted Date</th>
              <th>Deadline</th>
              <th>Uploader</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job.id}>
                <td className="job-title-cell">
                  <div className="job-title">{job.title_nl}</div>
                  <div className="job-title-en">{job.title_en}</div>
                </td>
                <td>
                  <span className="job-discipline-badge">{job.discipline}</span>
                </td>
                <td>{job.location}</td>
                <td>{job.salary}</td>
                <td>{formatDate(job.posted_date)}</td>
                <td>{job.deadline_date ? formatDate(job.deadline_date) : '-'}</td>
                <td>
                  {job.uploader_email ? getUploaderName(job.uploader_email) : '-'}
                </td>
                <td>
                  <div className="job-actions">
                    <button
                      onClick={() => onEdit(job)}
                      className="action-btn action-edit"
                      title="Edit"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(job.id)}
                      className="action-btn action-delete"
                      disabled={deletingId === job.id}
                      title="Delete"
                    >
                      {deletingId === job.id ? (
                        <div className="mini-spinner"></div>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobsTable;

